<template>
  <BlBackdrop
    title="Edit details"
    label="Update"
    :submitLoading="submitLoading"
    :disabled="isDisabledSubmit"
    @backdrop-close="$emit('close-modal')"
    @backdrop-action="onSubmit"
  >
    <div
      class="grid items-center grid-cols-6 gap-4 px-8"
      :style="{ width: '38rem' }"
    >
      <BlInputText
        class="col-span-6"
        :input="nameInput"
        @changed="inputHandler"
      />
      <BlSelectBox
        class="col-span-6"
        label="Code"
        asterisk
        disabled
        search
        v-model="codeIdInput"
        placeholder="Select curriculum"
        :options="codeOptions"
        @change="handleCodeChange"
      />
      <!--  -->
      <BlInputText
        class="col-span-6"
        :input="descriptionInput"
        @changed="inputHandler"
      />
    </div>
  </BlBackdrop>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import BlBackdrop from "../../../BlBackdrop.vue";
import BlInputText from "../../../BlInputText.vue";
import BlInputDate from "../../../BlInputDate.vue";
import BlInputTime from "../../../BlInputTime.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlSelectBox from "../../../BlSelectBox.vue";

export default {
  name: "EditTargetDetailsModal",
  components: {
    BlBackdrop,
    BlInputText,
    BlInputDate,
    BlInputTime,
    BlTextBody2,
    BlSelectBox
  },
  props: ["target", "task"],
  data() {
    return {
      submitLoading: false,
      nameInput: {
        label: "Name",
        placeholder: "Type name",
        model: "",
        asterisk: true
      },
      codeIdInput: "",
      typeInput: "",
      statusInput: "",
      statusOptions: [
        { value: "pending", label: "Pending" },
        { value: "in_progress", label: "In acquisition" },
        { value: "paused", label: "Paused" },
        { value: "mastered", label: "Mastered" },
        { value: "discontinued", label: "Discontinued" }
      ],
      dateIntroduceInput: {
        label: "Date introduced",
        model: ""
      },
      dateMasteredInput: {
        label: "Date mastered",
        model: "",
        disabled: true
      },
      descriptionInput: {
        label: "Description",
        placeholder: "Type the target description",
        model: "",
        textarea: true
      },
      defaultPrompts: [],
      loadingTasks: false
    };
  },
  computed: {
    ...mapGetters(["tasksCodes"]),
    detailsTarget() {
      if (this.target) {
        const target = {
          id: this.target.id,
          title: this.nameInput.model,
          code_id: this.task.task_code_id,
          status: this.statusInput,
          date_introduce: this.dateIntroduceInput.model,
          date_mastered: this.dateMasteredInput.model,
          description: this.descriptionInput.model
        };

        return JSON.parse(JSON.stringify(target));
      }
      return null;
    },

    codeOptions() {
      if (!this.tasksCodes || !this.tasksCodes.length) {
        return [];
      }
      return this.tasksCodes.map(i => ({
        value: i.id,
        label: i.code
      }));
    },
    isDisabledSubmit() {
      // Basic checks
      if (
        !this.detailsTarget.title ||
        !this.detailsTarget.code_id ||
        !this.detailsTarget.status
      ) {
        return true;
      }
      return false;
    }
  },
  watch: {
    target: {
      deep: true,
      handler(val) {
        this.generateData(val);
      }
    },
    statusInput(val) {
      if (val === "mastered") {
        const currentDateMastered = this.target.date_mastered;
        this.dateMasteredInput.model =
          currentDateMastered !== null
            ? currentDateMastered
            : moment().format("YYYY-MM-DD");
        this.dateMasteredInput.disabled = false;
      }
    },
    tasksCodes: {
      handler(newVal) {
        if (newVal && newVal.length && this.target && this.target.id) {
          if (!this.task && this.task && this.task.task_code_id) {
            this.codeIdInput = this.task.task_code_id;
          }
        }
      }
    },
    codeIdInput(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        const isAvailable = this.checkCodeAvailability(newVal);
        if (!isAvailable) {
          this.codeIdInput = oldVal;
        }
      }
    }
  },
  async mounted() {
    await this.fetchTaskCodes();
    this.generateData(this.target, this.task);
  },
  methods: {
    ...mapActions(["updateTarget", "showToast", "getTasksCodes"]),

    checkCodeAvailability(codeId) {
      if (this.task && this.task.task_code_id === codeId) {
        return true;
      }

      if (this.target && this.target.target_tasks) {
        const isCodeUsed = this.target.target_tasks.some(
          task => task.task_code_id === codeId && task.id !== this.task.id
        );

        if (isCodeUsed) {
          this.showToast({
            title: "Code Already in Use",
            message:
              "This code is already being used by another task in this target. Please select a different code.",
            type: "error"
          });
          return false;
        }
      }

      return true;
    },
    handleCodeChange(event) {
      const isAvailable = this.checkCodeAvailability(event);
      if (isAvailable) {
        this.codeIdInput = event;
      }
    },
    async fetchTaskCodes() {
      this.loadingTasks = true;
      await this.getTasksCodes();
      this.loadingTasks = false;
    },
    generateData(val = null, task = null) {
      if (val) {
        this.nameInput.model = task.title;
        this.codeIdInput = task.task_code_id;
        this.statusInput = task.status;
        this.descriptionInput.model = task.description;
        if (task.date_introduce) {
          this.dateIntroduceInput.model = moment(task.date_introduce).format(
            "YYYY-MM-DD"
          );
        } else {
          this.dateIntroduceInput.model = "";
        }
        if (task.date_mastered) {
          this.dateMasteredInput.model = moment(task.date_mastered).format(
            "YYYY-MM-DD"
          );
          this.dateMasteredInput.disabled = false;
        } else {
          this.dateMasteredInput.model = "";
          this.dateMasteredInput.disabled = true;
        }
      }
    },
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async onSubmit() {
      const params = {
        id: this.target.id,
        target: {
          target_tasks_attributes: [
            {
              id: this.task.id,
              task_code_id: this.detailsTarget.code_id,
              title: this.detailsTarget.title,
              description: this.detailsTarget.description,
              status: this.detailsTarget.status,
              date_introduce: this.detailsTarget.date_introduce,
              date_mastered: this.detailsTarget.date_mastered
            }
          ]
        }
      };

      this.submitLoading = true;
      const { success } = await this.updateTarget(params);
      this.submitLoading = false;
      if (!success) return;

      const title = "Target updated";
      const message = "Success! The target has been updated.";
      this.showToast({ title, message, type: "success" });
      this.$emit("close-modal");
    }
  }
};
</script>

<style scoped lang="scss"></style>



// WEBPACK FOOTER //
// src/components/clients/target/modal/EditTaskDetailsModal.vue